import { diff_match_patch, DIFF_EQUAL, DIFF_INSERT } from 'diff-match-patch';

/**
 * JSON取り込みの行単位差分（#3039）。
 *
 * 取り込み前後のJSONを GitHub のファイル差分のように行単位で並べ、
 * どこが変わったかを直感的に確認できるようにする。
 *
 * diff-match-patch は文字単位の差分が既定のため、
 * diff_linesToChars_ で「1行 = 1文字」に符号化してから差分を取り、行単位に戻す。
 */

export type LineDiffStatus = 'equal' | 'insert' | 'delete';

export interface LineDiffRow {
  status: LineDiffStatus;
  /** 変更前の行番号（1始まり）。追加行では undefined */
  beforeLineNumber?: number;
  /** 変更後の行番号（1始まり）。削除行では undefined */
  afterLineNumber?: number;
  text: string;
}

/**
 * diff-match-patch の行モード用API。型定義に含まれないため実行時のプロパティとして扱う。
 * diff_linesToChars_ は行を一意な文字へ符号化し、diff_charsToLines_ で復元する。
 */
interface LineModeApi {
  diff_linesToChars_: (
    text1: string,
    text2: string,
  ) => { chars1: string; chars2: string; lineArray: string[] };
  diff_charsToLines_: (diffs: [number, string][], lineArray: string[]) => void;
}

/**
 * 変更前後のテキストを行単位で比較し、表示用の行配列を返す。
 *
 * @param beforeText 変更前のJSON文字列
 * @param afterText 変更後のJSON文字列
 */
export const buildJsonLineDiff = (beforeText: string, afterText: string): LineDiffRow[] => {
  const dmp = new diff_match_patch();
  const lineApi = dmp as unknown as LineModeApi;

  // 行を文字へ符号化してから差分を取る（行単位の差分にするため）
  const { chars1, chars2, lineArray } = lineApi.diff_linesToChars_(beforeText, afterText);
  const diffs = dmp.diff_main(chars1, chars2, false) as [number, string][];
  lineApi.diff_charsToLines_(diffs, lineArray);

  const rows: LineDiffRow[] = [];
  let beforeLine = 1;
  let afterLine = 1;

  diffs.forEach(([op, text]) => {
    // 末尾の改行で空要素ができるため取り除く
    const lines = text.split('\n');
    if (lines[lines.length - 1] === '') {
      lines.pop();
    }

    lines.forEach(line => {
      if (op === DIFF_EQUAL) {
        rows.push({
          status: 'equal',
          beforeLineNumber: beforeLine++,
          afterLineNumber: afterLine++,
          text: line,
        });
        return;
      }
      if (op === DIFF_INSERT) {
        rows.push({ status: 'insert', afterLineNumber: afterLine++, text: line });
        return;
      }
      rows.push({ status: 'delete', beforeLineNumber: beforeLine++, text: line });
    });
  });

  return rows;
};

/**
 * 変更行の前後に残す文脈行の数。GitHub の既定（3行）に合わせる。
 */
const CONTEXT_LINES = 3;

/** 折りたたみの区切りを表す行 */
export interface CollapsedMarker {
  type: 'collapsed';
  /** 省略した行数 */
  hiddenCount: number;
}

export type DiffViewRow = ({ type: 'line' } & LineDiffRow) | CollapsedMarker;

/**
 * 変更のない行が続く箇所を折りたたみ、変更箇所とその周辺だけを残す。
 * 項目数の多いパターンでも変更点を見つけやすくするため。
 */
export const collapseUnchangedRows = (rows: LineDiffRow[]): DiffViewRow[] => {
  // 変更行から CONTEXT_LINES 以内の行は表示対象として残す
  const keep = new Array<boolean>(rows.length).fill(false);
  rows.forEach((row, idx) => {
    if (row.status === 'equal') return;
    const start = Math.max(0, idx - CONTEXT_LINES);
    const end = Math.min(rows.length - 1, idx + CONTEXT_LINES);
    for (let i = start; i <= end; i++) {
      keep[i] = true;
    }
  });

  const result: DiffViewRow[] = [];
  let hiddenCount = 0;

  rows.forEach((row, idx) => {
    if (keep[idx]) {
      if (hiddenCount > 0) {
        result.push({ type: 'collapsed', hiddenCount });
        hiddenCount = 0;
      }
      result.push({ type: 'line', ...row });
      return;
    }
    hiddenCount++;
  });

  if (hiddenCount > 0) {
    result.push({ type: 'collapsed', hiddenCount });
  }

  return result;
};
